import React, { useState } from 'react';
import { UserProfile, useUser } from '@clerk/clerk-react';
import { sportsbookNames, sportsbookIcons } from '../utils/oddsUtils';

const ALL_BOOKS = Object.keys(sportsbookNames);

function SportsbooksPage() {
  const { user } = useUser();
  const savedBooks = user?.unsafeMetadata?.selectedBooks || [];
  const [selected, setSelected] = useState(savedBooks);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const toggle = (key) => {
    setSelected(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await user.update({ unsafeMetadata: { ...user.unsafeMetadata, selectedBooks: selected } });
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  const selectAll = () => { setSelected(ALL_BOOKS); setSaved(false); };
  const clearAll = () => { setSelected([]); setSaved(false); };

  return (
    <div style={{ padding: '8px 0', maxWidth: '520px' }}>
      <h2 style={{ fontSize: '20px', fontWeight: '700', margin: '0 0 8px 0', color: '#ffffff' }}>
        My Sportsbooks
      </h2>
      <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 20px 0', lineHeight: '1.5' }}>
        Select the sportsbooks you have accounts with. Enable "My Books" in the filter bar to
        show only bets from your sportsbooks.
      </p>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button onClick={selectAll} style={linkBtnStyle}>Select all</button>
        <span style={{ color: '#cbd5e0', fontSize: '12px' }}>·</span>
        <button onClick={clearAll} style={linkBtnStyle}>Clear all</button>
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#94a3b8' }}>
          {selected.length} / {ALL_BOOKS.length} selected
        </span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '8px',
        marginBottom: '24px'
      }}>
        {ALL_BOOKS.map(key => {
          const isSelected = selected.includes(key);
          const iconFile = sportsbookIcons[key];
          return (
            <div
              key={key}
              onClick={() => toggle(key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 12px',
                border: `1px solid ${isSelected ? '#00d9ff' : '#2d3748'}`,
                borderRadius: '8px',
                backgroundColor: isSelected ? 'rgba(0, 217, 255, 0.1)' : '#0f1419',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
            >
              <span style={{
                width: '14px',
                height: '14px',
                border: `1.5px solid ${isSelected ? '#00d9ff' : '#cbd5e0'}`,
                borderRadius: '3px',
                backgroundColor: isSelected ? '#00d9ff' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: '9px',
                color: '#000',
                fontWeight: 'bold'
              }}>
                {isSelected && '✓'}
              </span>
              {iconFile ? (
                <img
                  src={`/sportsbook-icons/${iconFile}`}
                  alt={sportsbookNames[key]}
                  style={{ width: '20px', height: '20px', objectFit: 'contain', borderRadius: '4px', flexShrink: 0 }}
                />
              ) : (
                <div style={{
                  width: '20px', height: '20px', borderRadius: '4px', flexShrink: 0,
                  backgroundColor: '#e2e8f0', border: '1.5px solid #f97316'
                }} />
              )}
              <span style={{ fontSize: '13px', color: '#ffffff', fontWeight: isSelected ? '600' : '400' }}>
                {sportsbookNames[key]}
              </span>
            </div>
          );
        })}
      </div>

      <button
        onClick={save}
        disabled={saving}
        style={{
          padding: '10px 28px',
          background: saving ? '#94a3b8' : 'linear-gradient(135deg, #00d9ff 0%, #10b981 100%)',
          border: 'none',
          borderRadius: '8px',
          color: saving ? '#fff' : '#0a0e1a',
          fontSize: '14px',
          fontWeight: '700',
          cursor: saving ? 'not-allowed' : 'pointer',
          transition: 'opacity 0.2s'
        }}
      >
        {saving ? 'Saving...' : saved ? 'Saved!' : 'Save'}
      </button>
    </div>
  );
}

const linkBtnStyle = {
  background: 'none',
  border: 'none',
  padding: '0',
  fontSize: '12px',
  color: '#00b4d8',
  cursor: 'pointer',
  fontWeight: '600'
};

// Icon for the Sportsbooks tab in Clerk's sidebar
function BookIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}

function AccountSettings() {
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#0a0e1a', display: 'flex', justifyContent: 'center', paddingTop: '60px', paddingBottom: '60px' }}>
      <UserProfile
        appearance={{
          variables: {
            colorBackground: '#1a1f2e',
            colorInputBackground: '#0f1419',
            colorInputText: '#ffffff',
            colorText: '#ffffff',
            colorTextSecondary: '#a0aec0',
            colorNeutral: '#a0aec0',
            colorPrimary: '#00d9ff',
            borderRadius: '10px'
          },
          elements: {
            card: { boxShadow: 'none', border: '1px solid #2d3748', borderRadius: '16px', backgroundColor: '#1a1f2e' },
            navbar: { borderRight: '1px solid #2d3748', backgroundColor: '#1a1f2e' },
            navbarButton: { color: '#a0aec0' },
            navbarButtonActive: { color: '#ffffff' },
            pageScrollBox: { backgroundColor: '#1a1f2e' },
            profileSectionContent: { color: '#ffffff' },
            profileSectionTitle: { color: '#ffffff', borderBottom: '1px solid #2d3748' },
            profileSectionTitleText: { color: '#ffffff' },
            formFieldLabel: { color: '#a0aec0' },
            formFieldInput: { backgroundColor: '#0f1419', border: '1px solid #2d3748', color: '#ffffff' },
            formButtonPrimary: { backgroundColor: '#00d9ff', color: '#0a0e1a', fontWeight: '700' },
            formButtonReset: { color: '#a0aec0', borderColor: '#2d3748' },
            badge: { backgroundColor: 'rgba(0, 217, 255, 0.1)', color: '#00d9ff', border: '1px solid rgba(0, 217, 255, 0.3)' },
            accordionTriggerButton: { color: '#a0aec0' },
            menuList: { backgroundColor: '#1a1f2e', border: '1px solid #2d3748' },
            menuItem: { color: '#a0aec0' },
            avatarImageActionsUpload: { color: '#00d9ff' },
            headerTitle: { color: '#ffffff' },
            headerSubtitle: { color: '#a0aec0' }
          }
        }}
      >
        <UserProfile.Page label="Sportsbooks" url="sportsbooks" labelIcon={<BookIcon />}>
          <SportsbooksPage />
        </UserProfile.Page>
      </UserProfile>
    </div>
  );
}

export default AccountSettings;
